/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { CommandBarButton, IButtonProps, IconButton } from '@fluentui/react/lib/Button';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { BasicModalProps } from 'src/core/components/BasicModal';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { RenameModal } from 'src/core/components/RenameModal';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useDocumentEventHandler } from 'src/core/hooks/useDocumentEventHandler';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logButtonClick, logPivotClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { EntityStore } from 'src/core/types/EntityStore';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { noop } from 'src/core/utils/base';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { pivotKeyToPivotTitle } from 'src/core/utils/telemetryUtils';
import { CustomTextGptPredictionsStore } from 'src/customText/core/stores/CustomTextGptPredictionsStore';

export type PalettePivotType = 'tags' | 'distribution' | 'recommendations' | 'azureML';
export type EntityDto = { id: string; name: string; color: string };
type EntityPaletteProps = {
    id?: string;
    readonly?: boolean;
    isLoading?: boolean;
    entities: EntityStore[];
    cluProjectName?: string;
    projectId?: string;
    selectedEntityId?: string;
    isBrushDisabled?: boolean;
    addEntityButtonId?: string;
    autoLabelButtonId?: string;
    headerElement?: JSX.Element;
    footerElement?: JSX.Element;
    onUnselectEntity?: () => void;
    openPivot?: PalettePivotType;
    isAddEntityDisabled?: boolean;
    OnAutoLabelClick?: () => void;
    labelsCount: Map<string, number>;
    maxEntityNameCharLength?: number;
    checkDeleteEntity?: () => boolean;
    areEntityDetailsDisplayed?: boolean;
    tagsPivotHeaderElement?: JSX.Element;
    onEditEntity?: (entityId: string) => void;
    isTriggerAutoTaggingJobDisabled?: boolean;
    onAddEntity?: (entityName: string) => void;
    onRenderAzureMLTab?: () => React.ReactNode;
    onSelectEntity?: (entityId: string) => void;
    onDeleteEntity?: (entityId: string) => void;
    gptStoreHouse?: CustomTextGptPredictionsStore;
    onRenderDistributionTab: () => React.ReactNode;
    onRenderRecommendationTab?: () => React.ReactNode;
    getEntityDetailsText?: (entity: EntityDto) => string;
    onOpenPivotChange?: (newPivot: PalettePivotType) => void;
    onRenameEntity?: (entityId: string, newEntityName: string) => void;
    onRenderAddEntityModal?: (props: BasicModalProps<{ name: string }>) => JSX.Element;
};

const messages = defineMessages({
    yes: { id: 'customText.entityPalette.yes', defaultMessage: 'Yes' },
    edit: { id: 'customText.entityPalette.edit', defaultMessage: 'Edit' },
    rename: { id: 'customText.entityPalette.rename', defaultMessage: 'Rename' },
    delete: { id: 'customText.entityPalette.delete', defaultMessage: 'Delete' },
    title: { id: 'customText.entityPalette.title', defaultMessage: 'Activity pane' },
    highlight: { id: 'customText.entityPalette.highlight', defaultMessage: 'Highlight' },
    highlightBtnPressed: { id: 'customText.entityPalette.highlightBtnPressed', defaultMessage: 'Highlight button pressed' },
    autoLabel: { id: 'customText.entityPalette.autoLabel', defaultMessage: 'Auto-label' },
    addEntity: { id: 'customText.entityPalette.addEntity', defaultMessage: 'Add entity' },
    tagsTabHeader: { id: 'customText.entityPalette.tagsTabHeader', defaultMessage: 'Labels' },
    moreOptions: { id: 'customText.entityPalette.moreOptions', defaultMessage: 'More options' },
    emptySearchMsg: { id: 'customText.entityPalette.emptySearchMsg', defaultMessage: 'No entities found.' },
    labelingWithGpt: { id: 'customText.entityPalette.labelingWithGpt', defaultMessage: 'Labeling with GPT' },
    addEntityModalTitle: { id: 'customText.entityPalette.addEntityModalTitle', defaultMessage: 'Entity name' },
    distributionTabHeader: { id: 'customText.entityPalette.distributionTabHeader', defaultMessage: 'Distribution' },
    deleteModalTitle: { id: 'customText.entityPalette.deleteModalTitle', defaultMessage: 'Delete selected entity' },
    azureMLTabHeader: { id: 'customText.entityPalette.AzureMLTabHeader', defaultMessage: 'Azure Machine Learning' },
    emptyStateMsg: { id: 'customText.entityPalette.emptyStateMsg', defaultMessage: "You haven't created any entities." },
    recommendationsTabHeader: { id: 'customText.entityPalette.recommendationsTabHeader', defaultMessage: 'Recommendations' },
    searchFieldPlaceholder: { id: 'customText.entityPalette.searchFieldPlaceholder', defaultMessage: 'Search for an entity...' },
    deleteModalDescription: {
        id: 'customText.entityPalette.deleteModalDescription',
        defaultMessage: 'Are you sure you want to delete "{entityName}" entity?'
    },
    duplicatedEntityNameErrorMessage: {
        id: 'customText.entityPalette.duplicatedEntityNameErrorMessage',
        defaultMessage: 'This name already exists.'
    }
});

const PalettePadding = '15px';
const scopedTheme = getScopedTheme('EntityPalette');
const { styleComponent, getClassName, styleSpan } = getStylistV2('EntityPalette');

const menuIconClass = getClassName('menuIconClass', { display: 'none' });
const HighlightButton = styleComponent<IButtonProps>(IconButton)(
    'HighlightButton',
    (_, cssVars) => ({ backgroundColor: cssVars.bgColor }),
    {
        cssVars: { bgColor: '' },
        cssVarsSetter: ({ isClicked }: { isClicked: boolean }) => ({
            bgColor: isClicked ? scopedTheme.selectedHighlighterButtonColor : 'none'
        })
    }
);

const Loading = styleComponent<IStackProps>(Stack)('Loading', { padding: 12 });
const Pane = styleComponent<IStackProps>(Stack)('Pane', theme => ({
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    paddingBottom: '10px',
    borderLeft: theme.borderSpecs
}));

const ToolBar = styleComponent<IStackProps>(Stack)('ToolBar', theme => ({
    height: '42px',
    margin: '0px 10px 0 0',
    borderBottom: `solid 1px ${theme.separatorColor}`
}));

const ColorCode = styleSpan(
    'ColorCode',
    (_, cssVars) => ({
        width: 20,
        height: 20,
        marginRight: 8,
        borderRadius: '50%',
        border: '1px solid white',
        background: cssVars.color
    }),
    {
        cssVars: { color: '' },
        cssVarsSetter: ({ color }: { color: string }) => ({ color })
    }
);

const emptyMsgStyles = classNamesFunction<ITextProps, ITextStyles>()({
    root: { fontSize: '14px', textAlign: 'center', fontStyle: 'italic', padding: 5 }
});
const smallScreenPanelContainer = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { overflowY: 'auto !important' }
});
const entityNameStyles = classNamesFunction<ITextProps, ITextStyles>()({
    root: { whiteSpace: 'nowrap', flex: 2, overflow: 'hidden', textOverflow: 'ellipsis' }
});
const entityDetailsStyles = classNamesFunction<ITextProps, ITextStyles>()({
    root: { color: scopedTheme.entityDetailsColor, textTransform: 'uppercase', fontSize: '10px' }
});

export const EntityPalette = observer((props: EntityPaletteProps) => {
    const {
        entities,
        isLoading,
        labelsCount,
        onAddEntity,
        id = getId(),
        onEditEntity,
        headerElement,
        footerElement,
        gptStoreHouse,
        cluProjectName,
        projectId,
        onRenameEntity,
        onSelectEntity,
        onDeleteEntity,
        onUnselectEntity,
        selectedEntityId,
        OnAutoLabelClick,
        onOpenPivotChange,
        checkDeleteEntity,
        openPivot = 'tags',
        onRenderAzureMLTab,
        getEntityDetailsText,
        tagsPivotHeaderElement,
        onRenderAddEntityModal,
        readonly = false,
        isBrushDisabled = false,
        onRenderDistributionTab,
        maxEntityNameCharLength,
        onRenderRecommendationTab,
        isAddEntityDisabled = false,
        autoLabelButtonId = getId(),
        addEntityButtonId = getId(),
        areEntityDetailsDisplayed = false,
        isTriggerAutoTaggingJobDisabled = false
    } = props;
    const history = useHistory();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();
    const { isMediumOrGreater, isLargeOrGreater, isXLargeOrGreater } = useResponsiveMode();
    const isLabelingLocked = !!rootStore.currentCustomTextProjectStore?.isLabelingLocked;
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);
    const [query, setQuery] = React.useState('');
    const [editedEntityValue, setEditedEntityValue] = React.useState('');
    const [pivotTabsDisplay, setPivotTabsDisplay] = React.useState('block');
    const [entityToBeEdited, setEntityToBeEdited] = React.useState<EntityDto>(null);
    const [entityToBeDeleted, setEntityToBeDeleted] = React.useState<EntityDto>(null);
    const [selectedPivot, setSelectedPivot] = React.useState<PalettePivotType>(openPivot || 'tags');
    const [isAddNewEntityModalHidden, setIsAddNewEntityModalHidden] = React.useState(true);

    const isInEmptyState = entities.length === 0;
    const filteredEntities = substringSearchStrategy(entities, query, 'name');
    filteredEntities.sort((a, b) => (a.prebuilts.length === b.prebuilts.length ? 0 : a.prebuilts.length === 0 ? -1 : 1));

    const isDuplicatedEntityName = editedEntityValue && entities.find(e => e.name === editedEntityValue);
    const duplicatedEntityNameErrorMessage = isDuplicatedEntityName && localize(messages.duplicatedEntityNameErrorMessage);

    React.useEffect(() => {
        if (Boolean(openPivot) && openPivot !== selectedPivot) {
            setSelectedPivot(openPivot);
        }
    }, [openPivot]);

    React.useEffect(() => {
        if (isLargeOrGreater) {
            setPivotTabsDisplay('block');
        } else {
            setPivotTabsDisplay('grid');
        }
    }, [isLargeOrGreater]);

    const handleDeleteEntity = () => {
        onDeleteEntity(entityToBeDeleted.id);
        setEntityToBeDeleted(null);
        logButtonClick(serviceName, 'DataLabeling', 'DeleteEntity', 'Labels', projectId);
    };

    const handleAddEntity = (dto: { name: string }) => {
        onAddEntity(dto.name);
        setIsAddNewEntityModalHidden(true);
        logButtonClick(serviceName, 'DataLabeling', 'AddEntity', 'Labels', projectId);
    };

    const handleEntityEditDismiss = () => {
        setEditedEntityValue('');
        setEntityToBeEdited(null);
    };

    const handleEntityBrushSelection = (entityId: string) => {
        logButtonClick(serviceName, 'DataLabeling', 'Brush', 'Labels', projectId);

        if (entityId === selectedEntityId) {
            onUnselectEntity();
        } else {
            onSelectEntity(entityId);
        }
    };

    const handleEntityEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();

        switch (e.key) {
            case LuisKeyCodes.Escape:
                handleEntityEditDismiss();
                break;
            case LuisKeyCodes.Enter:
                if (isDuplicatedEntityName) {
                    break;
                } else if (editedEntityValue === entityToBeEdited.name) {
                    handleEntityEditDismiss();
                } else {
                    onRenameEntity(entityToBeEdited.id, editedEntityValue);
                    setEntityToBeEdited(null);
                    setEditedEntityValue('');
                }
                break;
            default:
        }
    };

    useDocumentEventHandler('keyup', e => {
        if (e.key === LuisKeyCodes.Escape) {
            handleEntityBrushSelection(selectedEntityId);
        }
    });

    const addEntityModalProps = {
        name: '',
        onSubmit: handleAddEntity,
        maxNameCharLength: maxEntityNameCharLength,
        hidden: isAddNewEntityModalHidden,
        existingEntityNames: entities.map(e => e.name),
        onDismiss: () => setIsAddNewEntityModalHidden(true),
        dialogContentProps: { title: localize(messages.addEntityModalTitle) }
    };

    const canEntityGetMoreOptionItems = onRenameEntity || onEditEntity;

    const setEditEntity = (entity: EntityDto) => {
        logButtonClick(serviceName, 'DataLabeling', 'EditEntity', 'Labels', projectId);
        setEntityToBeEdited(entity);
    };

    const handleOnEditEntity = (_id: string) => {
        logButtonClick(serviceName, 'DataLabeling', 'EditEntity', 'Labels', projectId);
        onEditEntity(_id);
    };

    const handleEntityDelete = (entity: EntityDto) => {
        if (checkDeleteEntity()) {
            setEntityToBeDeleted(entity);
        }
    };

    const getMoreOptionItems = (entity: EntityDto) =>
        (onRenameEntity ? [{ key: 'rename', text: localize(messages.rename), onClick: () => setEditEntity(entity) }] : [])
            .concat(onEditEntity ? [{ key: 'edit', text: localize(messages.edit), onClick: () => handleOnEditEntity(entity.id) }] : [])
            .concat([{ key: 'delete', text: localize(messages.delete), onClick: () => setEntityToBeDeleted(entity) }]);

    const handlePivotChange = (newPivotKey: PalettePivotType) => {
        logPivotClick(serviceName, 'DataLabeling', pivotKeyToPivotTitle(newPivotKey), projectId);
        setSelectedPivot(newPivotKey);
        onOpenPivotChange?.(newPivotKey);
    };

    return (
        <Pane grow id={id} styles={!isXLargeOrGreater && smallScreenPanelContainer}>
            {isLoading ? (
                <Loading>
                    <ShimmerFactory shimmerCount={12} shimmerSpacing={15} fullWidth />
                </Loading>
            ) : (
                <Stack verticalFill tokens={{ childrenGap: 5 }}>
                    {headerElement}

                    <Pivot
                        selectedKey={selectedPivot}
                        styles={{ root: { marginLeft: PalettePadding, display: pivotTabsDisplay } }}
                        overflowBehavior={'menu'}
                        overflowAriaLabel={localize(messages.moreOptions)}
                        onLinkClick={link => {
                            logPivotClick('ner', 'DataTagging', link.props.itemKey as PalettePivotType, projectId);
                            handlePivotChange(link.props.itemKey as PalettePivotType);
                        }}>
                        <PivotItem itemKey="tags" headerText={localize(messages.tagsTabHeader)} />
                        <PivotItem itemKey="distribution" headerText={localize(messages.distributionTabHeader)} />
                        {onRenderRecommendationTab && (
                            <PivotItem itemKey="recommendations" headerText={localize(messages.recommendationsTabHeader)} />
                        )}
                        {FeatureToggles.AMLIntegration && onRenderAzureMLTab ? (
                            <PivotItem itemKey="azureML" headerText={localize(messages.azureMLTabHeader)} />
                        ) : null}
                    </Pivot>

                    {selectedPivot === 'tags' && [
                        <StackItem>
                            {tagsPivotHeaderElement}
                            <ToolBar horizontal verticalFill horizontalAlign="space-between">
                                <Stack horizontal verticalFill>
                                    {!readonly && (
                                        <RBACTooltip
                                            displayedUserRole={displayedUserRole}
                                            hidden={canWrite || isAddEntityDisabled}
                                            data-automation-id="addEntityButtonLanguageToolTip"
                                            componentSurroundedByToolTip={
                                                <CommandBarButton
                                                    id={addEntityButtonId}
                                                    iconProps={{ iconName: 'Add' }}
                                                    onClick={() => setIsAddNewEntityModalHidden(false)}
                                                    styles={mergeStyleSets(getCommandBarButtonStyles(false))}
                                                    disabled={isLabelingLocked || !canWrite || isAddEntityDisabled}>
                                                    {localize(messages.addEntity)}
                                                </CommandBarButton>
                                            }
                                        />
                                    )}
                                    {OnAutoLabelClick && (
                                        <RBACTooltip
                                            hidden={canWrite}
                                            displayedUserRole={displayedUserRole}
                                            data-automation-id="startAutoTaggingJobButtonLanguageToolTip"
                                            componentSurroundedByToolTip={
                                                <CommandBarButton
                                                    role="menuitem"
                                                    id={autoLabelButtonId}
                                                    onClick={OnAutoLabelClick}
                                                    text={localize(messages.autoLabel)}
                                                    name={localize(messages.autoLabel)}
                                                    iconProps={{ iconName: 'AutoEnhanceOn' }}
                                                    styles={getCommandBarButtonStyles(false)}
                                                    disabled={!canWrite || isTriggerAutoTaggingJobDisabled || isLabelingLocked}
                                                />
                                            }
                                        />
                                    )}
                                </Stack>

                                <LuisDebouncedCollapsableSearchBox
                                    size={18}
                                    value={query}
                                    disabled={isInEmptyState}
                                    ariaLabel={localize(messages.searchFieldPlaceholder)}
                                    placeholder={localize(messages.searchFieldPlaceholder)}
                                    onChange={(_, newValue?: string) => setQuery(newValue)}
                                />
                            </ToolBar>
                        </StackItem>,
                        ...(gptStoreHouse?.tracker.isRunning
                            ? [
                                  <Stack horizontal tokens={{ childrenGap: 8, padding: '2px 15px' }}>
                                      <Spinner size={SpinnerSize.xSmall} />
                                      <LuisText color={scopedTheme.gptTextColor}>{localize(messages.labelingWithGpt)}</LuisText>
                                  </Stack>
                              ]
                            : []),
                        <Stack verticalFill verticalAlign="center" styles={{ root: { minHeight: 100, position: 'relative' } }}>
                            <ScrollablePane>
                                {isInEmptyState ? (
                                    <LuisText block styles={emptyMsgStyles}>
                                        {localize(messages.emptyStateMsg)}
                                    </LuisText>
                                ) : filteredEntities.length === 0 ? (
                                    <LuisText block styles={emptyMsgStyles}>
                                        {localize(messages.emptySearchMsg)}
                                    </LuisText>
                                ) : (
                                    filteredEntities.map(entity => (
                                        <Stack
                                            key={entity.id}
                                            verticalAlign="center"
                                            horizontal={isMediumOrGreater}
                                            horizontalAlign="space-between"
                                            styles={{ root: { padding: `5px ${PalettePadding}` } }}>
                                            <Stack horizontal verticalFill verticalAlign="center" styles={{ root: { overflow: 'hidden' } }}>
                                                <ColorCode
                                                    customProps={{
                                                        color: cluProjectName || entity.prebuilts.length === 0 ? entity.color : '#808080'
                                                    }}
                                                />
                                                {entityToBeEdited?.id === entity.id ? (
                                                    <LuisTextField
                                                        autoFocus
                                                        onBlur={handleEntityEditDismiss}
                                                        errorMessage={duplicatedEntityNameErrorMessage}
                                                        value={editedEntityValue || entityToBeEdited.name}
                                                        onKeyDown={onRenameEntity ? handleEntityEditKeyDown : noop}
                                                        onChange={(_, newEntityName) => setEditedEntityValue(newEntityName)}
                                                    />
                                                ) : (
                                                    <LuisText variant="medium" title={entity.name} styles={entityNameStyles}>
                                                        {cluProjectName ? (
                                                            <NameCellLink
                                                                onClick={() =>
                                                                    history.push(
                                                                        routeTable.clu.entityDetailsPage(cluProjectName, entity.name)
                                                                    )
                                                                }>
                                                                {entity.name}
                                                            </NameCellLink>
                                                        ) : (
                                                            <>{entity.name}</>
                                                        )}
                                                        &nbsp;
                                                        {(cluProjectName || !entity.prebuilts.length) &&
                                                            `(${labelsCount.get(entity.id) ?? 0})`}{' '}
                                                        &nbsp;
                                                        {areEntityDetailsDisplayed && (
                                                            <LuisText styles={entityDetailsStyles}>{getEntityDetailsText(entity)}</LuisText>
                                                        )}
                                                    </LuisText>
                                                )}
                                            </Stack>
                                            {!readonly && (
                                                <Stack horizontal wrap={false}>
                                                    <RBACTooltip
                                                        displayedUserRole={displayedUserRole}
                                                        data-automation-id="highlightButtonLanguageToolTip"
                                                        hidden={canWrite || isBrushDisabled || gptStoreHouse?.tracker.isRunning}
                                                        componentSurroundedByToolTip={
                                                            <HighlightButton
                                                                aria-roledescription={entity.id === selectedEntityId ? '' : 'button'}
                                                                iconProps={{ iconName: 'Highlight' }}
                                                                ariaLabel={
                                                                    entity.id === selectedEntityId
                                                                        ? localize(messages.highlightBtnPressed)
                                                                        : localize(messages.highlight)
                                                                }
                                                                onClick={() => handleEntityBrushSelection(entity.id)}
                                                                customProps={{ isClicked: entity.id === selectedEntityId }}
                                                                disabled={
                                                                    !canWrite ||
                                                                    isBrushDisabled ||
                                                                    gptStoreHouse?.tracker.isRunning ||
                                                                    (!cluProjectName && entity.prebuilts.length !== 0)
                                                                }
                                                            />
                                                        }
                                                    />

                                                    <RBACTooltip
                                                        hidden={canWrite}
                                                        displayedUserRole={displayedUserRole}
                                                        data-automation-id="moreOptionsButtonLanguageToolTip"
                                                        componentSurroundedByToolTip={
                                                            canEntityGetMoreOptionItems ? (
                                                                <IconButton
                                                                    disabled={isLabelingLocked || !canWrite}
                                                                    iconProps={{ iconName: 'More' }}
                                                                    title={localize(messages.moreOptions)}
                                                                    ariaLabel={localize(messages.moreOptions)}
                                                                    menuIconProps={{ className: menuIconClass }}
                                                                    menuProps={{ items: getMoreOptionItems(entity) }}
                                                                />
                                                            ) : (
                                                                <IconButton
                                                                    disabled={
                                                                        isLabelingLocked ||
                                                                        !canWrite ||
                                                                        (!cluProjectName && entity.prebuilts.length !== 0)
                                                                    }
                                                                    iconProps={{ iconName: 'delete' }}
                                                                    title={localize(messages.delete)}
                                                                    ariaLabel={localize(messages.delete)}
                                                                    onClick={() => handleEntityDelete(entity)}
                                                                />
                                                            )
                                                        }
                                                    />
                                                </Stack>
                                            )}
                                        </Stack>
                                    ))
                                )}
                            </ScrollablePane>
                        </Stack>,
                        footerElement
                    ]}

                    {selectedPivot === 'distribution' && onRenderDistributionTab()}
                    {selectedPivot === 'recommendations' && onRenderRecommendationTab()}
                    {selectedPivot === 'azureML' && onRenderAzureMLTab?.()}
                </Stack>
            )}

            <DeletePromptModal
                onDelete={handleDeleteEntity}
                hidden={!entityToBeDeleted}
                submitButtonText={localize(messages.yes)}
                onDismiss={() => setEntityToBeDeleted(null)}
                dialogContentProps={{ title: localize(messages.deleteModalTitle) }}>
                {localize(messages.deleteModalDescription, { entityName: entityToBeDeleted?.name })}
            </DeletePromptModal>

            {onRenderAddEntityModal ? onRenderAddEntityModal({ ...addEntityModalProps }) : <RenameModal {...addEntityModalProps} />}
        </Pane>
    );
});
